import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/98 backdrop-blur-lg border-b border-background-200/50'
          : 'bg-background-50/95 backdrop-blur-md'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="text-foreground-950 font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap"
          >
            oopbuy.surf
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/article/how-to-use-oopbuy-spreadsheet"
              className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Shopping Guide
            </Link>
            <Link
              to="/article/oopbuy-spreadsheet-safety-guide"
              className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Safety
            </Link>
            <Link
              to="/article/oopbuy-spreadsheet-vs-pandabuy"
              className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Comparison
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="relative hidden md:block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-4 py-2.5 bg-background-100 hover:bg-background-200 rounded-lg text-foreground-700 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap border border-background-200/70"
            >
              <i className="ri-grid-line"></i>
              <span>Categories</span>
              {dropdownOpen ? <i className="ri-arrow-up-s-line text-base transition-transform duration-200"></i> : <i className="ri-arrow-down-s-line text-base transition-transform duration-200"></i>}
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex btn-primary text-xs md:text-sm px-4 md:px-6"
          >
            oopbuy spreadsheet
          </a>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex btn-outline text-xs md:text-sm px-4 md:px-6"
          >
            buy it now
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 transition-colors duration-200 cursor-pointer"
            aria-label="Toggle mobile menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors duration-150 cursor-pointer"
            >
              <i className="ri-home-4-line text-lg"></i>
              Home
            </Link>
            <Link
              to="/article/how-to-use-oopbuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors duration-150 cursor-pointer"
            >
              <i className="ri-book-open-line text-lg"></i>
              Shopping Guide
            </Link>
            <Link
              to="/article/oopbuy-spreadsheet-safety-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors duration-150 cursor-pointer"
            >
              <i className="ri-shield-check-line text-lg"></i>
              Safety Guide
            </Link>
            <Link
              to="/article/oopbuy-spreadsheet-vs-pandabuy"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors duration-150 cursor-pointer"
            >
              <i className="ri-scales-3-line text-lg"></i>
              Comparison
            </Link>
            <Link
              to="/article/oopbuy-spreadsheet-tips-tricks"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors duration-150 cursor-pointer"
            >
              <i className="ri-lightbulb-line text-lg"></i>
              Tips & Tricks
            </Link>

            <div className="border-t border-background-200/70 my-2"></div>
            <p className="px-3 py-2 text-foreground-400 text-xs font-semibold uppercase tracking-wider">Product Categories</p>

            <div className="max-h-64 overflow-y-auto space-y-1">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors duration-150 cursor-pointer text-left"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}